import { paginatedQuery } from 'lib-shared-api-utils';
import type {
  CreateCustomCardIntegrationApiArg,
  CreateCustomCardIntegrationApiResponse,
  CreateEmailDomainApiArg,
  CreateEmailDomainApiResponse,
  CreateEmailIntegrationApiArg,
  CreateEmailIntegrationApiResponse,
  DeleteCustomCardIntegrationApiArg,
  DeleteEmailIntegrationApiArg,
  DeleteEmailIntegrationApiResponse,
  ReadCustomCardIntegrationByIdApiArg,
  ReadCustomCardIntegrationByIdApiResponse,
  ReadCustomCardIntegrationsApiResponse,
  ReadCustomCardIntegrationsSanitizedApiResponse,
  ReadCustomCardTestIntegrationApiArg,
  ReadCustomCardTestIntegrationApiResponse,
  ReadEmailDomainsApiResponse,
  ReadEmailIntegrationApiArg,
  ReadEmailIntegrationApiResponse,
  ReadEmailIntegrationDefaultTemplateApiResponse,
  ReadEmailIntegrationDomainsApiResponse,
  ReadEmailIntegrationsApiArg,
  ReadEmailIntegrationsApiResponse,
  ResultApiResponse,
  UpdateCustomCardIntegrationApiArg,
  UpdateCustomCardIntegrationApiResponse,
  UpdateCustomCardTestIntegrationApiArg,
  UpdateCustomCardTestIntegrationApiResponse,
  UpdateEmailIntegrationApiArg,
  UpdateEmailIntegrationApiResponse,
  UpdatePixelTrackingStatusApiArg,
  UpdatePixelTrackingStatusApiResponse,
} from 'lib-shared-types-api';

import { api } from 'app/app.api';

import { transformCustomCardIntegrationArg } from './utils/transformArg';
import {
  transformCustomCardIntegrationByIdResponse,
  transformCustomCardIntegrationsResponse,
} from './utils/transformResponse';

export const integrationsApi = api
  .enhanceEndpoints({
    addTagTypes: ['CustomCard', 'EmailDomains'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      readEmailIntegrations: builder.query<
        ReadEmailIntegrationsApiResponse,
        ReadEmailIntegrationsApiArg
      >({
        query: (arg) =>
          paginatedQuery(
            arg,
            () => ({
              version: 2,
              path: 'integrations/email',
            }),
            { search: { supportsSortedSearch: true } },
          ),
      }),
      readEmailIntegration: builder.query<
        ReadEmailIntegrationApiResponse,
        ReadEmailIntegrationApiArg
      >({
        query: ({ emailAddress }) => ({
          version: 1,
          path: `integrations/email/${encodeURIComponent(emailAddress)}`,
        }),
      }),
      createEmailIntegration: builder.mutation<
        CreateEmailIntegrationApiResponse,
        CreateEmailIntegrationApiArg
      >({
        query: (body) => ({
          version: 1,
          method: 'POST',
          path: 'integrations/email',
          body,
        }),
      }),
      updateEmailIntegration: builder.mutation<
        UpdateEmailIntegrationApiResponse,
        UpdateEmailIntegrationApiArg
      >({
        query: ({ emailAddress, patch: body }) => ({
          version: 1,
          method: 'PATCH',
          path: `integrations/email/${emailAddress}`,
          body,
        }),
      }),
      deleteEmailIntegration: builder.mutation<
        DeleteEmailIntegrationApiResponse,
        DeleteEmailIntegrationApiArg
      >({
        query: ({ emailAddress }) => ({
          version: 1,
          method: 'DELETE',
          path: `integrations/email/${emailAddress}`,
        }),
      }),
      readEmailIntegrationDomains: builder.query<
        ReadEmailIntegrationDomainsApiResponse,
        void
      >({
        query: () => ({
          version: 1,
          path: 'integrations/email/domains',
        }),
      }),
      readCustomCardIntegrationById: builder.query<
        ReadCustomCardIntegrationByIdApiResponse,
        ReadCustomCardIntegrationByIdApiArg
      >({
        query: ({ hookId }) => ({
          version: 3,
          path: `integrations/customcards/${hookId}`,
        }),
        transformResponse: transformCustomCardIntegrationByIdResponse,
        providesTags: ['CustomCard'],
      }),
      readCustomCardIntegrations: builder.query<
        ReadCustomCardIntegrationsApiResponse,
        void
      >({
        query: () => ({
          version: 1,
          path: 'integrations/customcard',
        }),
        transformResponse: transformCustomCardIntegrationsResponse,
        providesTags: ['CustomCard'],
      }),
      readCustomCardIntegrationsSanitized: builder.query<
        ReadCustomCardIntegrationsSanitizedApiResponse,
        void
      >({
        query: () => ({
          version: 3,
          path: 'integrations/customcards/sanitized',
        }),
        providesTags: ['CustomCard'],
      }),
      createCustomCardIntegration: builder.mutation<
        CreateCustomCardIntegrationApiResponse,
        CreateCustomCardIntegrationApiArg
      >({
        query: (body) => ({
          version: 1,
          path: 'integrations/customcard',
          method: 'PUT',
          body: transformCustomCardIntegrationArg(body),
        }),
        invalidatesTags: ['CustomCard'],
      }),
      updateCustomCardIntegration: builder.mutation<
        UpdateCustomCardIntegrationApiResponse,
        UpdateCustomCardIntegrationApiArg
      >({
        query: (body) => ({
          version: 1,
          path: 'integrations/customcard',
          method: 'PUT',
          body: transformCustomCardIntegrationArg(body),
        }),
        invalidatesTags: ['CustomCard'],
      }),
      deleteCustomCardIntegration: builder.mutation<
        ResultApiResponse,
        DeleteCustomCardIntegrationApiArg
      >({
        query: ({ hookId }) => ({
          version: 1,
          path: `integrations/customcard/${hookId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['CustomCard'],
      }),
      readCustomCardTestIntegration: builder.query<
        ReadCustomCardTestIntegrationApiResponse,
        ReadCustomCardTestIntegrationApiArg
      >({
        query: ({ hookId, query = '' }) => {
          return {
            version: 1,
            path: `integrations/customcard/${hookId}${query}`,
          };
        },
        providesTags: ['CustomCard'],
      }),
      updateCustomCardTestIntegration: builder.mutation<
        UpdateCustomCardTestIntegrationApiResponse,
        UpdateCustomCardTestIntegrationApiArg
      >({
        query: ({ hookId, query = '', template }) => {
          return {
            version: 3,
            path: `integrations/customcards/${hookId}/test${query}`,
            method: 'PUT',
            body: { template },
          };
        },
      }),
      readEmailDomains: builder.query<ReadEmailDomainsApiResponse, void>({
        query: () => ({
          version: 2,
          path: 'email-identities',
        }),
        providesTags: ['EmailDomains'],
      }),
      createEmailDomain: builder.mutation<
        CreateEmailDomainApiResponse,
        CreateEmailDomainApiArg
      >({
        query: ({ domain }) => ({
          version: 2,
          path: `email-identity/${domain}`,
          method: 'PUT',
        }),
        invalidatesTags: ['EmailDomains'],
      }),
      updatePixelTrackingStatus: builder.mutation<
        UpdatePixelTrackingStatusApiResponse,
        UpdatePixelTrackingStatusApiArg
      >({
        query: ({ identityId, enabled }) => ({
          version: 2,
          path: `email-identity/${identityId}/${
            enabled ? 'enable-pixel-tracking' : 'disable-pixel-tracking'
          }`,
          method: 'PUT',
        }),
        invalidatesTags: ['EmailDomains'],
      }),
      readEmailIntegrationDefaultTemplate: builder.query<
        ReadEmailIntegrationDefaultTemplateApiResponse,
        void
      >({
        query: () => ({
          version: 1,
          path: 'integrations/email/templates/default',
          method: 'GET',
        }),
      }),
    }),
  });
